const EventEmitter = require('events').EventEmitter;

class JsonRPC extends EventEmitter {

  constructor({
    url = null,
    options = null,
    beforeHandle = null,
    beforeSend = null,
    WebSocket,
    seq = Math.round(Math.random() * 10000000)
  } = {}) {
    super();

    this._seq = seq;
    this._reqRecords = new Map();
    this._reqHandlers = new Map();
    this._notifyHandlers = new Map();
    this._beforeHandle = beforeHandle;
    this._beforeSend = beforeSend;
    this._WebSocket = WebSocket;

    if (!this._beforeHandle) {
      this._beforeHandle = message => {
        return JSON.parse(message);
      }
    }

    this._url = url;
    this._options = options;

    this.state = JsonRPC.CREATE;
  }

  /**
   * @throws {Error}
   */
  connect() {
    const socket = new this._WebSocket(this._url, this._options);

    socket.onerror = (e) => {
      this.state = JsonRPC.CLOSING;

      this.emit("error", e);
    }

    socket.onclose = (e) => this._emitClose(e)

    socket.onopen = () => {
      this.socket = socket;
    }
  }

  get socket() {
    return this._socket;
  }

  set socket(socket) {

    this._removeSocketListeners();

    if (socket && socket.readyState === socket.OPEN) {
      this._socket = socket;
      this._addSocketListeners();
      this.state = JsonRPC.OPEN;
      this.emit('open');
    }
  }

  get beforeHandle() {
    return this._beforeHandle;
  }

  set beforeHandle(handler) {
    if (handler === null) {
      this._beforeHandle = message => {
        return JSON.parse(message);
      }
    } else if (typeof handler !== 'function') {
      throw (new Error('handler is not a function'));
    } else {
      this._beforeHandle = handler;
    }
  }

  get beforeSend() {
    return this._beforeSend;
  }

  set beforeSend(handler) {
    if (handler === null) {
      this._beforeSend = null;
    } else if (typeof handler !== 'function') {
      throw (new Error('handler is not a function'));
    } else {
      this._beforeSend = handler;
    }
  }

  /**
   * 
   * @param {*} method 
   * @param {*} params 
   * @throws {ERROR}
   */
  async request(method, params = null, timeout = 8000) { // 8s timeout

    const req = {
      jsonrpc: "2.0",
      id: this._seq++,
      method,
      params
    }

    this.send(req);

    return new Promise((_resolve, _reject) => {

      const reqRecord = {
        id: req.id,
        method: req.method,

        timer: setTimeout(() => {
          clearTimeout(reqRecord.timer);
          _reject(JsonRPC.ERROR.REQUEST_TIMEOUT);
        }, timeout),

        resolve: (result) => {
          clearTimeout(reqRecord.timer);
          _resolve(result);
        },

        reject: (error) => {
          clearTimeout(reqRecord.timer);
          _reject(error);
        },

        close: () => {
          clearTimeout(reqRecord.timer);
          _reject(JsonRPC.ERROR.CLOSED);
        }
      };

      this._reqRecords.set(req.id, reqRecord);
    });
  }

  /**
   * 
   * @param {*} method 
   * @param {*} listener (param, accept, reject) => {}
   */
  onRequest(method, listener) {

    if (!listener) {
      this._reqHandlers.delete(method);
      return;
    }
    this._reqHandlers.set(method, listener);
  }

  notify(method, params) {

    const notify = {
      jsonrpc: "2.0",
      method: method,
      params
    }

    try {
      this.send(notify);
    } catch (e) {
      return false;
    }

    return true;
  }

  onNotify(method, listener) {

    if (!listener) {
      this._notifyHandlers.delete(method);
      return;
    }
    this._notifyHandlers.set(method, listener);
  }

  close() {
    if (this.state === JsonRPC.CLOSED || this.state === JsonRPC.CLOSING) {
      return;
    }

    // close req.
    this._reqRecords.forEach(reqRecord => {
      reqRecord.close();
    });

    if (this._socket) {

      this.state = JsonRPC.CLOSING;
      this._socket.close();
    }
  }

  /**
   * 
   * @param {*} message 
   * @throws {ERROR} send in wrong state
   */
  send(message) {
    if (this.state !== JsonRPC.OPEN) {
      throw JsonRPC.ERROR.INVALID_STATU;
    }

    if (this._beforeSend) {
      message = this._beforeSend(message);
    }

    if (message) {
      return this._socket.send(JSON.stringify(message));
    }
  }

  async onmessage(message) {

    message = this._beforeHandle(message);

    if (!message || (typeof message !== 'object')) {
      return;
    }

    // @todo(present): check jsonrpc version

    const id = message.id;

    if (id !== undefined) { // request|response

      const method = message.method;

      if (!method) { // a response

        const error = message.error;
        const result = message.result;

        const reqRecord = this._reqRecords.get(id);

        if (reqRecord) {

          this._reqRecords.delete(id);

          if (error) {
            reqRecord.reject(error);
          } else {
            reqRecord.resolve(result);
          }
        }

        return;
      }

      // a request

      const params = message.params;

      const reqHandler = this._reqHandlers.get(method);

      const reject = ({
        code,
        message,
        data = null
      }) => {

        const response = {
          jsonrpc: "2.0",
          id,
          error: {
            code,
            message,
            data
          }
        };

        try {
          this.send(response);
        } catch (e) {
          return false;
        }

        return true;
      };

      if (reqHandler) {

        const accept = (result = null) => {

          const response = {
            jsonrpc: "2.0",
            id,
            result
          };

          try {
            this.send(response);
          } catch (e) {
            return false;
          }

          return true;
        };

        reqHandler(params, accept, reject);
      } else {
        reject(JsonRPC.ERROR.INVALID_METHOD);
      }

    } else { // a notify

      const method = message.method;
      const params = message.params;

      const notifyHandler = this._notifyHandlers.get(method);

      if (notifyHandler) {
        notifyHandler(params);
      }
    }
  }

  _addSocketListeners() {
    this._socket.onmessage = (event) => this.onmessage(event.data);

    this._socket.onerror = (event) => {
      this.state = JsonRPC.CLOSING;

      this.emit("error", event)
    }
    this._socket.onclose = (event) => this._emitClose(event);
  }

  _removeSocketListeners() {
    if (this._socket) {
      this._socket.onmessage = null;

      this._socket.onerror = null;
      this._socket.onclose = null;
    }
  }

  _emitClose(event) {
    if (this.state === JsonRPC.CLOSED) {
      return;
    }

    this._removeSocketListeners();
    this._socket = null;

    this._reqRecords.forEach(reqRecord => {
      reqRecord.close();
    });

    this.state = JsonRPC.CLOSED;

    this.emit('close', event);
  }
}

JsonRPC.CREATE = 0;
JsonRPC.OPEN = 1;
JsonRPC.CLOSING = 2
JsonRPC.CLOSED = 3;


function E(code, message) {
  return {
    code,
    message
  };
}

JsonRPC.ERROR = {
  CLOSED: E(0, 'CLOSED'),
  INVALID_STATU: E(1, 'INVALID_STATU'),
  INVALID_METHOD: E(2, 'INVALID_METHOD'),
  REQUEST_TIMEOUT: E(3, 'REQUEST_TIMEOUT')
}

module.exports = JsonRPC;